import { IScore, IShow } from "@eagerdog/interfaces";

import { groupByBreed, groupByBreedGroup, partitionScores, streamScores } from "./partition";
import {
  conformationOf,
  readGroupPlacements,
  readStreamShow,
  savedBestOfBreed,
  savedNonLicensedBestOfBreed,
} from "./stored";
import { bestJuniorPool, juniorPlacementClasses } from "./junior";
import { CONFORMATION_STREAM_ORDER, ConformationStream, IPartitionedScores } from "./types";

export interface IStreamProgress {
  stream: ConformationStream;
  breeds: number;
  breedsWithBob: number;
}

export interface IConformationProgress {
  streams: IStreamProgress[];
  /** Regular breed groups and how many have a saved 1st. */
  groups: number;
  groupsWithFirst: number;
  /** Streams with entries vs. streams with a saved Best in Show. */
  bisStreams: number;
  bisSaved: number;
  /** Junior: whether Best Junior Handler is saved and how many classes have a 1st. */
  juniorClasses: number;
  juniorClassesWithFirst: number;
  hasJuniors: boolean;
  bestJuniorSaved: boolean;
}

/** Breed-level completion for one stream: a breed counts when its BOB / Best Brace is saved. */
export function streamProgress(
  show: IShow,
  parts: IPartitionedScores,
  stream: ConformationStream
): IStreamProgress {
  const scores = streamScores(parts, stream);
  let breeds = 0;
  let breedsWithBob = 0;
  groupByBreed(scores).forEach((buckets) => {
    buckets.forEach((bucket) => {
      breeds += 1;
      const saved =
        stream === "regular" || stream === "altered"
          ? savedBestOfBreed(show, bucket.breed, bucket.breedGroup, stream)
          : savedNonLicensedBestOfBreed(show, bucket.breed, bucket.breedGroup, stream);
      if (saved) breedsWithBob += 1;
    });
  });
  return { stream, breeds, breedsWithBob };
}

export function conformationProgress(show: IShow, scores: IScore[]): IConformationProgress {
  const parts = partitionScores(scores);

  const streams = CONFORMATION_STREAM_ORDER.filter(
    (stream) => streamScores(parts, stream).length > 0
  ).map((stream) => streamProgress(show, parts, stream));

  const groupKeys = Array.from(groupByBreedGroup(parts.regular).keys());
  const groupsWithFirst = groupKeys.filter((group) => !!readGroupPlacements(show, group)[0]).length;

  const bisStreamsList = CONFORMATION_STREAM_ORDER.filter(
    (stream) => streamScores(parts, stream).length > 0
  );
  const bisSaved = bisStreamsList.filter((stream) => !!readStreamShow(show, stream).best_in_show)
    .length;

  const juniorClasses = juniorPlacementClasses(parts.junior);
  const saved = conformationOf(show)?.junior_showmanship;
  const juniorClassesWithFirst = juniorClasses.filter(
    (level) => !!saved?.class_results?.find((row) => row.level === level)?.placements?.[0]
  ).length;
  const hasBestJuniorPool = bestJuniorPool(parts.junior, {}).length > 0;

  return {
    streams,
    groups: groupKeys.length,
    groupsWithFirst,
    bisStreams: bisStreamsList.length,
    bisSaved,
    juniorClasses: juniorClasses.length,
    juniorClassesWithFirst,
    hasJuniors: parts.junior.length > 0,
    bestJuniorSaved: hasBestJuniorPool ? !!saved?.best_junior : true,
  };
}
